import { X } from "lucide-react";
import { type KeyboardEvent, useState } from "react";

/**
 * Chip-style free-text tag entry: type + Enter/comma to add, Backspace to remove
 * the last chip. The inner field is a deliberately borderless inline input (not
 * the bordered shared <Input>) so it sits flush inside the chip container — this
 * is why it lives here in components/admin rather than in a page (the admin-page
 * lint guard bans raw <input>).
 */
export function TagInput({
  values,
  onChange,
  placeholder,
}: {
  values: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
}) {
  const [input, setInput] = useState("");
  const addTag = (raw: string) => {
    const t = raw.trim();
    if (t && !values.includes(t)) onChange([...values, t]);
    setInput("");
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(input);
    } else if (e.key === "Backspace" && !input && values.length) onChange(values.slice(0, -1));
  };
  return (
    <div className="flex min-h-[38px] flex-wrap gap-1.5 rounded-lg border border-gray-300 p-2 focus-within:border-primary focus-within:ring-2 focus-within:ring-ring">
      {values.map((v) => (
        <span
          key={v}
          className="inline-flex items-center gap-1 rounded bg-green-100 px-2 py-0.5 text-sm text-green-800"
        >
          {v}
          <button
            type="button"
            onClick={() => onChange(values.filter((x) => x !== v))}
            className="hover:text-green-600"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <input
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={onKey}
        onBlur={() => addTag(input)}
        placeholder={values.length === 0 ? placeholder : ""}
        className="min-w-[100px] flex-1 bg-transparent text-sm text-gray-900 outline-none"
      />
    </div>
  );
}
